import React, { useMemo } from "react";

import useTranslations from "../../hooks/useTranslations";

import { H4, TailSpin } from "../wombat-ui";
import useGetReportAnswers from "../../graphql/hooks/queries/useGetReportAnswers";
import { StringParam, useQueryParams } from "use-query-params";
import {
  getReferentGradeAverage,
  getTalentUserData,
  ReportQuestionAverage,
} from "../../utils/madUtils";
import { ResponsiveLine } from "@nivo/line";
import { format } from "date-fns";
import { cloneDeep, last } from "lodash";

type EvaluationEvolutionDetailProps = {
  id: string;
  selectedDataset: "talent" | "referent";
};

const EvaluationEvolutionDetail: React.FC<EvaluationEvolutionDetailProps> = ({
  id,
  selectedDataset,
}) => {
  const t = useTranslations();

  const { data, loading } = useGetReportAnswers({
    variables: { templateQuestionDigest: [id] },
  });

  const [{ userId }] = useQueryParams({
    userId: StringParam,
  });

  const chartData = useMemo(() => {
    if (data) {
      const usersData = getTalentUserData(data.reportAnswers.collection);

      const userData = usersData.find(({ user }) => user.id === userId);

      if (userData) {
        const user = userData.user;

        let grade: Partial<ReportQuestionAverage>[];

        if (selectedDataset === "referent")
          grade = userData.reports.flatMap(({ reportAnswers }) => {
            return getReferentGradeAverage(
              reportAnswers,
              reportAnswers
                .filter((ra) => !ra.isTalent)
                .map(({ user }) => user.id)
            );
          });
        else
          grade = userData.reports.flatMap(({ reportAnswers }) => {
            const talentReportAnswer = reportAnswers.filter(
              (ra) => ra.isTalent
            );
            return {
              reportAnswers: talentReportAnswer,
              // This naming is only to match the getReferentGradeAverage API
              averageGrade: parseInt(talentReportAnswer?.[0].value.selector),
            };
          });

        return {
          id: grade?.[0]?.templateQuestionDigest,
          user,
          data: grade.map(({ reportAnswers, averageGrade }) => ({
            x: format(new Date(last(reportAnswers).updatedAt), "yyyy-MM-dd"),
            y: averageGrade,
          })),
        };
      }
    }
  }, [data, userId, selectedDataset]);

  return (
    <>
      {loading && <TailSpin />}
      {!loading && chartData && (
        <div className="flex flex-col flex-grow col-span-5 px-3 py-2 mx-12 mt-12 mb-6 space-y-4 bg-white rounded-md shadow-md h-96">
          <H4>
            {t("statistics.evaluationEvolutionOf", {
              value: chartData.user.name,
            })}
          </H4>
          <ResponsiveLine
            margin={{ top: 20, right: 80, bottom: 60, left: 80 }}
            animate
            enableArea
            enableSlices="x"
            pointSize={8}
            pointBorderColor="#fff"
            pointBorderWidth={2}
            data={chartData ? [chartData] : []}
            xScale={{
              type: "time",
              format: "%Y-%m-%d",
              useUTC: false,
              precision: "day",
            }}
            xFormat="time:%d-%m-%Y"
            yScale={{
              type: "linear",
              min: 0,
              max: 4,
            }}
            axisBottom={{
              tickRotation: 45,
              format: "%b %d",
              tickValues: "every 14 days",
            }}
            sliceTooltip={({ slice }) => {
              return (
                <div className="rounded-md shadow-md bg-dark-100 text-light-0">
                  {slice.points.map((point) => {
                    return (
                      <div className="" key={point.id}>
                        <div className="px-5 py-4 mb-4 rounded-t-md">
                          <h5 className="text-light-300">
                            {t("mads.reportDated")} {point.data.xFormatted}
                          </h5>
                          <p className="text-sm text-light-500">
                            {t("statistics.rating", {
                              count: slice.points.length,
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            }}
            curve="monotoneX"
            enablePointLabel={true}
            useMesh={true}
          />
        </div>
      )}
    </>
  );
};

export default React.memo(EvaluationEvolutionDetail);
